<script setup lang="ts">
import { defineProps,Ref,inject,ref, computed, onMounted } from 'vue'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { sendGraphQLQuery } from '@/services/gql-util'

interface Transfer {
	mid: number
	status: string
	updated: string
	media: { title: string }
	transfer: { name: string }
}

const loading = ref(false)
const error = ref<string | null>(null)
const transfers = ref<Transfer[]>([])
const vhostId = inject<Ref<string>>('vhostId');

const statusTabs = [
	{ value: 'queued', label: 'Queued' },
	{ value: 'success', label: 'Success' },
	{ value: 'failure', label: 'Failed' }
]
const props = defineProps<{
	size?: 'small' | 'medium' | 'large'
	height?: number
}>()

const divHeight = computed(() => {
	if (props.height) {
		if (props.size === 'small') {
			return (props.height * 100) + 'px';
		} else if (props.size === 'medium') {
			return (props.height * 200) + 'px';
		} else if (props.size === 'large') {
			return (props.height * 400) + 'px';
		}
	}
	return '100px';
});
const fetchRecentTransfers = async () => {
	loading.value = true
	error.value = null

	try {
		const query = `
      query GetRecentTransfers($vhost: Int!) {
	Transfers {
	  recent: filetransfers(vhost: $vhost, limit: 15) {
	    mid
	    status
	    updated
	    media { title }
	    transfer { name }
	  }
	}
      }
    `

		const response = await sendGraphQLQuery<{ 
			Transfers: { recent: Transfer[] } 
		}>(query, { vhost:    vhostId && vhostId.value ? parseInt(vhostId.value):0 })

		transfers.value = response.Transfers.recent
		console.log(transfers.value);
	} catch (err) {
		error.value = err instanceof Error ? err.message : 'Failed to fetch transfers'
		console.error('Error fetching transfers:', err)
	} finally {
		loading.value = false
	}
}

const filteredTransfers = (status: string) => {
	return transfers.value
		.filter(t => t.status.toLowerCase() === status.toLowerCase())
		.slice(0, 5) // Show only last 5 for each status
}

const formatTime = (dateString: string) => {
	return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
	fetchRecentTransfers()
})
</script>

<template>
	<Card class="w-full" :style="'height:'+divHeight">
	<Tabs defaultValue="queued">
	<CardHeader class="border-b p-0">
	<TabsList class="w-full p-0">
	<TabsTrigger v-for="tab in statusTabs" :key="tab.value" :value="tab.value" class="flex-1 py-3 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium transition-colors"  :class="{ 'p-1 text-body-sm': size === 'small' }"> 
	{{ tab.label }}
	</TabsTrigger>
	</TabsList>

	</CardHeader>
	<CardContent class="bg-card p-2">
	<div v-if="loading" class="p-3 text-center text-sm text-muted-foreground">
		Loading transfers...
	</div>
	<div v-else-if="error" class="p-3 text-center text-sm text-destructive">
		{{ error }}
	</div>
	<template v-else>
		<TabsContent v-for="tab in statusTabs" :key="`content-${tab.value}`" :value="tab.value" class="p-0" >
		<div v-if="filteredTransfers(tab.value).length === 0" class="p-4 text-center text-sm text-muted-foreground">
			No {{ tab.label.toLowerCase() }} transfers
		</div>
		<ul v-else class="divide-y">
			<li v-for="transfer in filteredTransfers(tab.value)" :key="transfer.mid" class="p-1 hover:bg-accent/50 transition-colors" >
				<div class="flex justify-between items-start gap-2">
					<div class="truncate">
						<p class="text-body font-normal truncate">{{ transfer.media.title }}</p>
						<p class="text-body-sm text-muted-foreground truncate" v-if="props.size!='small'">{{ transfer.transfer.name }}</p>
					</div>
					<div class="flex flex-col items-end shrink-0">
						<Badge :variant="tab.value === 'success' ? 'default' : tab.value === 'failure' ? 'destructive' : 'secondary'" class="text-xs" > {{ transfer.mid }} </Badge>
						<span class="text-xs text-muted-foreground mt-1" v-if="props.size!='small'">
							{{ formatTime(transfer.updated) }}
						</span>
					</div>
				</div>
			</li>
		</ul>
		</TabsContent>
	</template>
	</CardContent>
	</Tabs>
	</Card>
</template>

<style scoped>
:deep(.tabs-content) {
	padding: 0 !important;
}
</style>
